import { formatPhoneForWhatsApp } from '../utils/validation';

export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Время жизни токена в миллисекундах (24 часа)
const TOKEN_EXPIRATION_MS = 24 * 60 * 60 * 1000;

/**
 * Проверяет, истёк ли токен
 */
const isTokenExpired = (): boolean => {
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    if (!tokenExpiry) return true;
    return Date.now() > parseInt(tokenExpiry, 10);
};

/**
 * Сохраняет время истечения токена
 */
export const setTokenExpiry = (): void => {
    const expiryTime = Date.now() + TOKEN_EXPIRATION_MS;
    localStorage.setItem('tokenExpiry', expiryTime.toString());
};

/**
 * Обработка ошибок авторизации (401/403)
 * Очищает токены и перенаправляет на страницу авторизации
 */
const handleUnauthorized = (): void => {
    console.log('Токен истёк или недействителен. Выполняем logout...');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('user');
    
    // Перенаправляем на страницу авторизации
    if (window.location.pathname !== '/auth') {
        window.location.href = '/auth';
    }
};

/**
 * Проверяет токен перед запросом и обрабатывает ошибки 401/403
 */
const checkTokenAndHandleError = async (response: Response): Promise<Response> => {
    if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
    }
    return response;
};

/**
 * Проверяет токен перед выполнением запроса
 * Если токен истёк, делает logout
 */
const ensureValidToken = (): boolean => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
    if (!token) {
        return false;
    }
    
    if (isTokenExpired()) {
        handleUnauthorized();
        return false;
    }
    
    return true;
};

// Contract API
export const contractApi = {
    getAll: async () => {
        if (!ensureValidToken()) throw new Error('Сессия истекла');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/contracts`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });
        await checkTokenAndHandleError(response);
        if (!response.ok) throw new Error('Failed to fetch contracts');
        return response.json();
    },

    getById: async (id: number) => {
        if (!ensureValidToken()) throw new Error('Сессия истекла');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/contracts/${id}`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });
        await checkTokenAndHandleError(response);
        if (!response.ok) throw new Error('Failed to fetch contract');
        return response.json();
    },

    create: async (data: { number: string; companyId: number; filePath: string }) => {
        if (!ensureValidToken()) throw new Error('Сессия истекла');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/contracts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify(data),
        });
        await checkTokenAndHandleError(response);
        if (!response.ok) throw new Error('Failed to create contract');
        return response.json();
    },

    sign: async (id: number, signatureData: { signatureData: string; certificateData: string; signatoryId: number }) => {
        if (!ensureValidToken()) throw new Error('Сессия истекла');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/contracts/${id}/sign`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify(signatureData),
        });
        await checkTokenAndHandleError(response);
        if (!response.ok) throw new Error('Failed to sign contract');
        return response.json();
    },

    verify: async (id: number) => {
        if (!ensureValidToken()) throw new Error('Сессия истекла');
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/contracts/${id}/verify`, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            }
        });
        await checkTokenAndHandleError(response);
        if (!response.ok) throw new Error('Failed to verify signature');
        return response.json();
    }
};

// Existing API exports (keeping existing functionality)
export const api = {
    // Auth endpoints
    sendOtp: async (phone: string) => {
        // Only format phone numbers, not email addresses
        const formattedPhone = phone.includes('@')
            ? phone  // Keep email as is
            : formatPhoneForWhatsApp(phone);  // Format phone for WhatsApp
        const response = await fetch(`${API_BASE_URL}/auth/send-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ login: formattedPhone }),
        });
        return response.json();
    },

    verifyOtp: async (phone: string, otp: string) => {
        // Only format phone numbers, not email addresses
        const formattedPhone = phone.includes('@')
            ? phone  // Keep email as is
            : formatPhoneForWhatsApp(phone);  // Format phone for WhatsApp
        const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ login: formattedPhone, otpCode: otp }),
        });
        return { data: await response.json() };
    },

    register: async (userData: any) => {
        const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
        });
        return response.json();
    },

    // Company endpoints
    registerCompany: async (companyData: any) => {
        const token = localStorage.getItem('accessToken');
        console.log('Registering company with data:', companyData);
        console.log('Using token:', token ? 'present' : 'missing');

        const response = await fetch(`${API_BASE_URL}/companies`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(companyData),
        });

        console.log('Company registration response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.log('Company registration error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Company registration success:', data);
        return { data };
    },

    // Document endpoints
    uploadDocument: async (formData: FormData) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData,
        });
        return { data: await response.json() };
    },

    checkBin: async (bin: string) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/companies/check-bin/${bin}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.log('Check BIN error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return { data: await response.json() };
    },

    // Auth token management
    getAuthToken: () => {
        return localStorage.getItem('adminToken') || localStorage.getItem('accessToken') || localStorage.getItem('authToken');
    },

    setAuthToken: (token: string) => {
        localStorage.setItem('accessToken', token);
    },

    removeAuthToken: () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authToken');
    },

    // Auth endpoints
    login: async (credentials: { login: string; password: string }) => {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
        });
        const data = await response.json();
        if (data.accessToken) {
            localStorage.setItem('accessToken', data.accessToken);
            // Сохраняем время истечения токена (24 часа)
            setTokenExpiry();
        }
        return { data };
    },

    logout: async () => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            await fetch(`${API_BASE_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
        }
    },

    getCurrentUser: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    // Company endpoints
    getMyCompany: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/companies/my`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return { data: await response.json() };
    },

    // Проверка компании из 1С для доступа к заявкам на покупку
    checkCompanyFrom1C: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/companies/my`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const companyData = await response.json();
        return {
            data: {
                from1C: companyData.from1C || false,
                hasBankingDetails: !!(companyData.bankName && companyData.bankBik && companyData.bankAccount),
                company: companyData
            }
        };
    },

    updateCompany: async (id: number, companyData: any) => {
        const token = localStorage.getItem('accessToken');
        console.log('Updating company', id, 'with data:', companyData);

        const response = await fetch(`${API_BASE_URL}/companies/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(companyData),
        });

        console.log('Company update response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.log('Company update error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Company update success:', data);
        return { data };
    },

    // Moderation endpoints
    submitForModeration: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/auth/submit-for-moderation`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({})
        });
        return { data: await response.json() };
    },

    resubmitApplication: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/auth/resubmit-application`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({})
        });
        return { data: await response.json() };
    },

    resetApplication: async () => {
        const token = localStorage.getItem('accessToken');
        console.log('Sending reset application request with token:', token ? 'present' : 'missing');

        const response = await fetch(`${API_BASE_URL}/auth/reset-application`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        console.log('Reset application response status:', response.status);
        console.log('Reset application response headers:', response.headers);

        if (!response.ok) {
            const errorText = await response.text();
            console.log('Error response text:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        return { data };
    },

    saveCompanyDraft: async (companyData: any) => {
        const token = localStorage.getItem('accessToken');
        console.log('Saving company draft with data:', companyData);
        console.log('Data fields:', {
            bin: companyData.bin,
            name: companyData.name,
            legalAddress: companyData.legalAddress,
            actualAddress: companyData.actualAddress,
            contactPhone: companyData.contactPhone,
            contactEmail: companyData.contactEmail,
            directorName: companyData.directorName
        });
        console.log('Using token:', token ? 'present' : 'missing');

        const response = await fetch(`${API_BASE_URL}/companies/save-draft`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(companyData),
        });

        console.log('Save draft response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.log('Save draft error response:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return { data: await response.json() };
    },

    // Admin endpoints for moderation
    getModerationRequests: async (params: any) => {
        const token = localStorage.getItem('accessToken');
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetch(`${API_BASE_URL}/admin/moderation/requests?${queryParams}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    getModerationRequestDetails: async (userId: number) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/admin/moderation/requests/${userId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    approveApplication: async (userId: number, comment: string) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/admin/moderation/requests/${userId}/approve`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ comment }),
        });
        return { data: await response.json() };
    },

    rejectApplication: async (userId: number, comment: string) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/admin/moderation/requests/${userId}/reject`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ reason: comment }),
        });
        return { data: await response.json() };
    },

    requestChanges: async (userId: number, comment: string) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/admin/moderation/requests/${userId}/request-changes`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ changes: comment }),
        });
        return { data: await response.json() };
    },

    getDocumentUrl: async (documentId: number) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/url`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    getModerationStats: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/admin/moderation/stats`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    // Document endpoints
    getMyDocuments: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/my`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    getDocumentTypes: async () => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/types`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    deleteDocument: async (documentId: number) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    downloadDocument: async (documentId: number) => {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            },
        });

        if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                // Если это JSON ответ (для локальных файлов)
                return { data: await response.json() };
            } else {
                // Если это бинарный файл (из S3)
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                return { data: { success: true, url: url, isBlob: true } };
            }
        } else {
            throw new Error('Ошибка скачивания документа');
        }
    },

    // OTP methods with corrected names and signatures
    sendOTP: async (data: { identifier: string }) => {
        // Only format phone numbers, not email addresses
        const formattedIdentifier = data.identifier.includes('@')
            ? data.identifier  // Keep email as is
            : formatPhoneForWhatsApp(data.identifier);  // Format phone for WhatsApp
        const response = await fetch(`${API_BASE_URL}/auth/send-login-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: formattedIdentifier }),
        });
        return { data: await response.json() };
    },

    verifyOTPLogin: async (data: { identifier: string; otpCode: string }) => {
        // Only format phone numbers, not email addresses
        const formattedIdentifier = data.identifier.includes('@')
            ? data.identifier  // Keep email as is
            : formatPhoneForWhatsApp(data.identifier);  // Format phone for WhatsApp
        const response = await fetch(`${API_BASE_URL}/auth/verify-login-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: formattedIdentifier, otpCode: data.otpCode }),
        });
        const responseData = await response.json();
        if (responseData.data && responseData.data.accessToken) {
            localStorage.setItem('authToken', responseData.data.accessToken);
            // Сохраняем время истечения токена (24 часа)
            setTokenExpiry();
        }
        return { data: responseData };
    },

    // Add contract API to main api object
    contracts: {
        ...contractApi,
        createTestContract: async () => {
            const response = await fetch(`${API_BASE_URL}/contracts/test`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            return response.json();
        },

        // Получить договоры текущего пользователя
        getMyContracts: async () => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/contracts/my`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                if (response.status === 403) {
                    // Токен истек или нет доступа - очищаем токены
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                }
                throw new Error('Failed to fetch my contracts');
            }
            return response.json();
        },

        // Автоматически сгенерировать договор для текущего пользователя
        autoGenerate: async () => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/contracts/auto-generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to auto generate contract');
            return response.json();
        },

        // Скачать договор в PDF
        download: async (contractId: number) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to download contract');

            // Если это PDF файл
            if (response.headers.get('content-type')?.includes('application/pdf')) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                return { success: true, url: url, isBlob: true };
            }

            return response.json();
        },

        // Подписать договор
        sign: async (contractId: number, signatureData: { signatureData: string; certificateData?: string; signatoryId?: number }) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/sign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(signatureData),
            });
            if (!response.ok) throw new Error('Failed to sign contract');
            return response.json();
        },

        // Отменить договор
        cancel: async (contractId: number) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/contracts/${contractId}/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to cancel contract');
            return response.json();
        }
    },

    // Заявки на покупку (Orders) API methods
    orders: {
        // Получить заявки пользователя
        getMyOrders: async () => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/orders/my-orders`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch orders');
            return response.json();
        },

        // Создать заявку
        createOrder: async (orderData: any) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/orders/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(orderData)
            });
            if (!response.ok) throw new Error('Failed to create order');
            return response.json();
        },

        // Получить заявку по orderId (строка типа PF-803659)
        getOrderById: async (orderId: string) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch order');
            return response.json();
        },

        // Обновить заявку
        updateOrder: async (orderId: number, orderData: any) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(orderData)
            });
            if (!response.ok) throw new Error('Failed to update order');
            return response.json();
        },

        // Удалить заявку
        deleteOrder: async (orderId: number) => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to delete order');
            return response.json();
        },

        // Получить доступные продукты
        getProducts: async () => {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/products`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch products');
            return response.json();
        }
    },

    // Profile API methods
    getProfile: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/me`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    updatePersonalData: async (data: any) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/personal-data`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        return { data: await response.json() };
    },

    updateCompanyInfo: async (data: any) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        console.log('Token for company update:', token ? 'exists' : 'missing');

        const response = await fetch(`${API_BASE_URL}/profile/company-info`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });

        console.log('Company update response status:', response.status);

        if (!response.ok) {
            let errorText;
            try {
                errorText = await response.text();
                console.error('Company info update error response:', errorText);
            } catch (e) {
                errorText = 'Failed to read error response';
            }

            if (response.status === 401) {
                // Токен истек или недействителен
                localStorage.removeItem('accessToken');
                localStorage.removeItem('authToken');
                window.location.href = '/auth';
                throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
            }

            throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        }

        return { data: await response.json() };
    },

    getBankingDetails: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/banking-details`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return { data: await response.json() };
    },

    getEmployees: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/employees`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return { data: await response.json() };
    },

    addEmployee: async (data: any) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/employees`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        return { data: await response.json() };
    },

    updateEmployee: async (employeeId: number, data: any) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/employees/${employeeId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        return { data: await response.json() };
    },

    removeEmployee: async (employeeId: number) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/employees/${employeeId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });
        return { data: await response.json() };
    },

    inviteEmployee: async (companyId: number, data: any) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/profile/invite-employee/${companyId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data),
        });
        return { data: await response.json() };
    },

    // Order endpoints
    createOrder: async (orderData: {
        items: Array<{
            guid?: string;
            productCode?: string;
            quantity: number;
        }>;
        deliveryAddress: string;
        deliveryDate: string;
        comment: string;
    }) => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/orders/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(orderData),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to create order');
        }

        return await response.json();
    },

    getMyOrders: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/orders/my-orders`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    },

    canCreateOrder: async () => {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/orders/can-create`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.json();
    },
};

// Default export for backward compatibility
export default api;